import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule } from '../../translate/translate.module';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { trigger, style, transition, animate, group } from '@angular/animations';


export interface MultiModalButton {
  text: string,
  role: 'cancel' | 'confirm',
  class: 'primary' | 'default' | 'error',
  handler?: () => void | Promise<void>,
  loadingInModal?: {
    enabled: boolean,
    text?: string[]
  },
  loadingInButton?: boolean,
  icon?: string,
  isLoadingInButton?: boolean // do not set this variable initially. It's for displaying the loading.
}

export interface MultiModalDataset {
  type: 'table' | 'list' | 'centered' | 'aligned-left',
  datas: MultiModalData[],
  hasColon?: boolean,
  padding?: string;
}
export interface MultiModalData {
  key: string,
  value: string | null,
  style?: string
}
export interface MultiModalMessage {
  text: string,
  align?: 'center' | 'justify' | 'left',
  customClass?: string,
  isTop: boolean
}

export interface MultiModalCheckbox {
  label: string;
  link?: {
    text: string;
    handler: () => void;
  };
  required: boolean;
  checked?: boolean;
}


@Component({
  selector: 'app-multi-modal',
  templateUrl: './multi-modal.component.html',
  styleUrls: ['./multi-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, LoadingSpinnerComponent, FormsModule],
  animations: [
    trigger('collapse', [
      transition(':leave', [
        group([
          animate('300ms ease-in-out', style({ height: '0px' })),
          animate('100ms ease-in-out', style({ opacity: '0' })),
        ])
      ])
    ]),
    trigger('loadingAnimation', [
      transition(':enter', [
        style({ 'max-height': '0px', 'opacity': 0, 'display': 'none' }),
        group([
          animate('1300ms 300ms ease-in-out', style({ 'display': 'block' })),
          animate('1300ms 300ms ease-in-out', style({ 'max-height': '1000px' })),
          animate('300ms 450ms ease-in-out', style({ 'opacity': '1' })),
        ])
      ])
    ])
  ]
})
export class MultiModalComponent implements OnInit {
  @Input() buttons: MultiModalButton[];
  @Input() title: string;
  @Input() dataset: MultiModalDataset;
  @Input() message: MultiModalMessage;
  @Input() gap: string;
  @Input() containerStyle: string;
  @Input() logo: string;
  @Input() messageBeforeLogo: MultiModalMessage;
  @Input() checkbox: MultiModalCheckbox;

  isLoadingInModal: boolean = false;
  loadingMessage: string[] | undefined = undefined;
  checkboxChecked: boolean = false;

  constructor(private modalCtrl: ModalController) {

  }

  buttonClicked: boolean = false; // only lets a button to be clicked once

  ngOnInit() {
    if (this.checkbox) {
      this.checkboxChecked = this.checkbox.checked || false;
    }
  }

  onCheckboxChange(event: any) {
    this.checkboxChecked = event.detail.checked;
    if (this.checkbox) {
      this.checkbox.checked = this.checkboxChecked;
    }
  }

  async buttonClick(button: MultiModalButton) {
    let result = null;
    if (this.buttonClicked) { return; }

    if (this.checkbox && this.checkbox.required && !this.checkboxChecked && button.role === 'confirm') {
      this.buttonClicked = false;
      return;
    }

    this.buttonClicked = true;

    if (button.loadingInButton) {
      button.isLoadingInButton = true;
    }

    if (button.loadingInModal?.enabled) {
      this.isLoadingInModal = true;
      this.loadingMessage = button.loadingInModal.text;
    }

    let errorMessage = null;

    try {
      result = await button.handler?.();
    } catch (error: any) {
      console.log(error)
      errorMessage = error?.error?.message || error?.message || 'Unexpected error';
    }

    button.isLoadingInButton = false;

    await this.modalCtrl.dismiss({
      role: button.role,
      error: errorMessage,
      result: result
    })
  }
}
